import React, { useState } from 'react';
import HttpInterceptor from '../lib/HttpInterceptor';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaRobot, FaTimes, FaCheckCircle, FaLock, FaComments } from 'react-icons/fa';
import { AIConsultantsData } from '../constants/aiAgents';

const AiEnrollmentModal = ({ isOpen, onClose, aiService }) => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (!isOpen || !aiService) return null;

    const getConsultantImage = (service) => {
        const normalizedType = service?.toLowerCase().replace(/\s+/g, '-');
        const aiAgent = AIConsultantsData.find(ai => ai.type === normalizedType);
        return aiAgent ? aiAgent.profileImage : "/assets/ai-agents/priya_representative.png";
    };
    
    const resolvedType = aiService.type || (aiService.service ? aiService.service.toLowerCase().replace(/\s+/g, '-') : 'general');
    const consultantImage = getConsultantImage(resolvedType);

    const handleEnroll = async () => {
        try {
            setLoading(true);
            const resolvedServiceType = aiService.type || (aiService.service ? aiService.service.toLowerCase().replace(/\s+/g, '-') : 'general');
            
            const response = await HttpInterceptor.post('/api/ai/enroll', {
                serviceType: resolvedServiceType
            });

            if (response.data?.success) {
                toast.success(`Successfully enrolled with ${aiService.name}!`);
                onClose();
                const chatRes = await HttpInterceptor.post('/api/ai/chat-sessions', {
                    serviceType: resolvedServiceType,
                    title: "New Session"
                });
                if (chatRes.data?.success) {
                    navigate(`/student/ai-chat/${chatRes.data.data._id}`);
                } else {
                    navigate('/student/services');
                }
            } else {
                toast.error(response.data?.message || 'Failed to enroll');
            }
        } catch (error) {
            // Pick up the service type again
            const resolvedServiceType = aiService.type || (aiService.service ? aiService.service.toLowerCase().replace(/\s+/g, '-') : 'general');

            // If already enrolled
            if (error.response?.status === 400 && error.response?.data?.message?.includes('Already enrolled')) {
                onClose();
                try {
                    const chatRes = await HttpInterceptor.post('/api/ai/chat-sessions', {
                        serviceType: resolvedServiceType,
                        title: "New Session"
                    });
                    if (chatRes.data?.success) {
                        navigate(`/student/ai-chat/${chatRes.data.data._id}`);
                    } else {
                        navigate('/student/my-ai');
                    }
                } catch (chatErr) {
                    navigate('/student/my-ai');
                }
            } else {
                toast.error('An error occurred during enrollment');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <FaComments className="w-5 h-5 hidden" />
                        <span className="text-2xl">&times;</span>
                    </button>

                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 backdrop-blur-md shadow-md bg-white border-2 border-white/50 overflow-hidden">
                        <img src={consultantImage} alt="AI Agent" className="w-full h-full object-cover" />
                    </div>
                    <h2 className="text-2xl font-bold mb-1">Meet {aiService.name}</h2>
                    <p className="text-purple-100">Your Personal AI Consultant</p>
                </div>

                <div className="p-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                        <h4 className="flex items-center gap-2 text-amber-800 font-bold mb-2">
                            <span className="text-amber-500">⚠️</span> Disclaimer
                        </h4>
                        <p className="text-sm text-amber-700 leading-relaxed">
                            This is an Artificial Intelligence agent designed to provide general guidance and information.
                            <strong> It is NOT a licensed medical professional, doctor, or certified expert.</strong> Do not use this advice for medical emergencies or critical life decisions.
                        </p>
                    </div>

                    <div className="space-y-3 mb-6">
                        <h4 className="font-semibold text-gray-800">What you get:</h4>
                        <ul className="space-y-2">
                            <li className="flex items-start gap-2 text-sm text-gray-600">
                                <span className="text-green-500 mt-0.5">✓</span>
                                Instant, 24/7 access to expert-level knowledge
                            </li>
                            <li className="flex items-start gap-2 text-sm text-gray-600">
                                <span className="text-green-500 mt-0.5">✓</span>
                                Personalized consultations and guidance
                            </li>
                            <li className="flex items-start gap-2 text-sm text-gray-600">
                                <span className="text-green-500 mt-0.5">✓</span>
                                Free* daily queries (Shared limit, subject to change)
                            </li>
                            <li className="flex items-start gap-2 text-sm text-gray-600">
                                <span className="text-gray-400 mt-0.5">🔒</span>
                                Private and secure conversations
                            </li>
                        </ul>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEnroll}
                            disabled={loading}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-purple-200 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Enrolling...' : 'Accept & Enroll'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiEnrollmentModal;
